import { format } from "date-fns";
import { NavLink } from "react-router-dom";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, MarkEmailRead, RemoveRedEye } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import { notificationLink, notificationActionLabel } from "../../features/notifications/notificationAudiences";
import { Notification } from "../../features/process/types/processTypes";

type NotificationTableProps = {
  notifications: Notification[];
  loading: boolean;
  currentPage: number;
  rowsPerPage: number;
  showActions?: boolean;
  canMarkRead?: boolean;
  canDelete?: boolean;
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
};

export function NotificationTable({
  notifications,
  loading,
  currentPage,
  rowsPerPage,
  showActions = true,
  canMarkRead = false,
  canDelete = false,
  onMarkAsRead,
  onDelete,
}: NotificationTableProps) {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");

  const employeeLink = (notification: Notification) => notificationLink(notification);

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="notifications table">
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>{t("sno")}</StyledTableCell>
            <StyledTableCell>{t("partner")}</StyledTableCell>
            <StyledTableCell>{t("email")}</StyledTableCell>
            <StyledTableCell>{t("employee")}</StyledTableCell>
            <StyledTableCell>{t("actions")}</StyledTableCell>
            <StyledTableCell>{t("date")}</StyledTableCell>
            {showActions && (canMarkRead || canDelete) && (
              <StyledTableCell align="right">{t("manage")}</StyledTableCell>
            )}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeleton
              numRows={5}
              numColumns={showActions ? 7 : 6}
              showActions={showActions}
            />
          ) : notifications.length === 0 ? (
            <StyledTableRow>
              <StyledTableCell
                colSpan={showActions && (canMarkRead || canDelete) ? 7 : 6}
                align="center"
              >
                <Typography variant="body2" color="textSecondary">
                  {t("noNotifications")}
                </Typography>
              </StyledTableCell>
            </StyledTableRow>
          ) : (
            notifications.map((row, index) => (
              <StyledTableRow key={row._id ?? index}>
                <StyledTableCell>
                  {currentPage * rowsPerPage + index + 1}
                </StyledTableCell>
                <StyledTableCell>{row.from?.displayName}</StyledTableCell>
                <StyledTableCell>{row.fromUserEmail}</StyledTableCell>
                <StyledTableCell>
                  {row.wallPost?.message
                    ? row.wallPost.message.slice(0, 40)
                    : row.employee?.givenName}
                </StyledTableCell>
                <StyledTableCell>
                  {notificationActionLabel(
                    row.message,
                    row.contextText,
                    te,
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  {row.createdAt
                    ? format(
                        new Date(row.createdAt as Date),
                        "dd-MMM-yyyy hh:mm",
                      ).toLocaleUpperCase()
                    : ""}
                </StyledTableCell>
                {showActions && (canMarkRead || canDelete) && (
                  <TableCell align="right">
                    <Tooltip title={t("view")}>
                      <IconButton
                        color="primary"
                        component={NavLink}
                        to={employeeLink(row)}
                        size="small"
                      >
                        <RemoveRedEye />
                      </IconButton>
                    </Tooltip>
                    {canMarkRead && onMarkAsRead && (
                      <Tooltip title={t("markAsRead")}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => onMarkAsRead(row._id as string)}
                        >
                          <MarkEmailRead />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && onDelete && (
                      <Tooltip title={t("delete")}>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => onDelete(row._id as string)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </StyledTableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
